/**
 * ErvaPOS - Customers Page
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Plus,
  Search,
  Edit,
  Users,
  Loader2,
  Save,
  Gift,
  ShoppingBag,
  Euro,
  Receipt,
  FileText,
  Mail
} from 'lucide-react';
import { customersAPI, vouchersAPI, salesAPI } from '../services/api';
import { buildReceiptPreviewPdfFromElement } from '../lib/receiptPreviewPdf';
import SelectCustomerModal from '../components/SelectCustomerModal';
import { toast } from 'sonner';

const PAYMENT_LABELS = {
  cash: 'Dinheiro',
  card: 'Cartão',
  multibanco: 'Multibanco',
  transfer: 'Transferência',
  mbway: 'MBWay',
  account_current: 'Conta Corrente',
  voucher: 'Voucher',
  other: 'Outro',
  credit_card: 'Cartão Crédito',
};

const initialFormState = {
  name: '',
  phone: '',
  email: '',
  nif: '',
  address: '',
  notes: '',
};

export const CustomersPage = () => {
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerHistory, setCustomerHistory] = useState([]);
  const [formData, setFormData] = useState(initialFormState);
  const [voucherForm, setVoucherForm] = useState({ value: 5, is_percentage: false });
  const [saving, setSaving] = useState(false);

  const [selectedSale, setSelectedSale] = useState(null);
  const [showSaleDetail, setShowSaleDetail] = useState(false);
  const [saleEmail, setSaleEmail] = useState('');
  const [sendingSaleEmail, setSendingSaleEmail] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [completedSaleId, setCompletedSaleId] = useState(null);
  const [isFiscalSale, setIsFiscalSale] = useState(false);
  const [receiptPdfUrl, setReceiptPdfUrl] = useState('');
  const [receiptPdfLoading, setReceiptPdfLoading] = useState(false);
  const receiptPreviewRef = useRef(null);

  const [phoneResolveOpen, setPhoneResolveOpen] = useState(false);
  const [phoneResolveMatches, setPhoneResolveMatches] = useState([]);
  const [phoneResolvePhone, setPhoneResolvePhone] = useState('');
  const [forcedCustomerId, setForcedCustomerId] = useState(null);
  const [smsAutofocusHandled, setSmsAutofocusHandled] = useState(false);

  const onlyDigits = (v) => String(v || '').replace(/\D+/g, '');

  const normalizeTextPT = (v) =>
    String(v || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const normPhonePT = useCallback((v) => {
    const d = onlyDigits(v);
    if (!d) return '';
    if (d.startsWith('351') && d.length >= 12) return `+351${d.slice(-9)}`;
    if (d.length === 9 && d.startsWith('9')) return `+351${d}`;
    return d;
  }, []);

  const looksLikePhoneQuery = useCallback((q) => {
    const d = onlyDigits(q);
    if (d.length >= 12 && d.startsWith('351')) return true;
    if (d.length === 9) return true;
    if (d.length >= 9 && q.includes('+')) return true;
    return false;
  }, []);

  const getCustomerSearchScore = (customer, rawQuery) => {
    const query = normalizeTextPT(rawQuery);
    if (!query) return 0;

    const queryTokens = query.split(' ').filter(Boolean);
    const nameNorm = normalizeTextPT(customer?.name);
    const emailNorm = normalizeTextPT(customer?.email);
    const nifNorm = String(customer?.nif || '').trim();
    const phoneDigits = onlyDigits(customer?.phone);
    const queryDigits = onlyDigits(rawQuery);

    let score = 0;

    if (nameNorm === query) score += 1000;
    if (nameNorm.startsWith(query)) score += 700;
    if (nameNorm.includes(query)) score += 500;

    for (const token of queryTokens) {
      if (nameNorm === token) score += 250;
      else if (nameNorm.startsWith(token)) score += 180;
      else if (nameNorm.includes(token)) score += 120;

      if (emailNorm && emailNorm.includes(token)) score += 60;
      if (nifNorm && nifNorm.includes(token)) score += 80;
    }

    const allTokensMatchName =
      queryTokens.length > 1 && queryTokens.every(token => nameNorm.includes(token));
    if (allTokensMatchName) score += 350;

    if (queryDigits) {
      if (phoneDigits === queryDigits) score += 900;
      else if (phoneDigits.includes(queryDigits)) score += 300;

      if (nifNorm === queryDigits) score += 850;
      else if (nifNorm.includes(queryDigits)) score += 250;
    }

    return score;
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const response = await customersAPI.getAll({ limit: 20000 });
      setCustomers(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };


    useEffect(() => {
      let t = null;

      const run = async () => {
        try {
          if (!searchQuery) return;
          if (!looksLikePhoneQuery(searchQuery)) return;

          const phone = normPhonePT(searchQuery);
          if (!phone) return;

          const res = await customersAPI.resolveByPhone(phone);
          const data = res?.data || {};
          const matches = data.matches || [];

          // Se houver 2+ pessoas com o mesmo telefone, perguntar sempre
          if (matches.length >= 2) {
            setPhoneResolvePhone(phone);
            setPhoneResolveMatches(matches);
            setPhoneResolveOpen(true);
          } else {
            setPhoneResolveOpen(false);
            setPhoneResolveMatches([]);
            setPhoneResolvePhone('');
          }
        } catch (e) {
          // não chatear o utilizador por isto, é só ajuda de UI
        }
      };

      // debounce leve
      t = setTimeout(run, 350);

      return () => {
        if (t) clearTimeout(t);
      };
    }, [searchQuery, looksLikePhoneQuery, normPhonePT]);

  useEffect(() => {
    if (smsAutofocusHandled || loading || !Array.isArray(customers) || customers.length === 0) {
      return;
    }

    const params = new URLSearchParams(window.location.search || '');
    const smsCustomerId = String(params.get('sms_customer_id') || '').trim();
    const smsQuery = String(params.get('sms_q') || '').trim();

    if (!smsCustomerId && !smsQuery) {
      setSmsAutofocusHandled(true);
      return;
    }

    if (smsQuery) {
      setSearchQuery(smsQuery);
    }

    const matchedCustomer = smsCustomerId
      ? customers.find((customer) => String(customer?.id || '') === smsCustomerId)
      : null;

    if (matchedCustomer) {
      setEditingCustomer(matchedCustomer);
      setFormData({
        name: matchedCustomer.name || '',
        phone: matchedCustomer.phone || '',
        email: matchedCustomer.email || '',
        nif: matchedCustomer.nif || '',
        address: matchedCustomer.address || '',
        notes: matchedCustomer.notes || '',
      });
      setShowModal(true);
      toast.info('Ficha de cliente aberta para adicionar ou corrigir o telemóvel.');
    }

    setSmsAutofocusHandled(true);
  }, [customers, loading, smsAutofocusHandled]);

  const baseFilteredCustomers = !searchQuery
    ? [...customers].sort((a, b) =>
        String(a?.name || '').localeCompare(String(b?.name || ''), 'pt', { sensitivity: 'base' })
      )
    : customers
        .map((customer) => ({
          customer,
          score: getCustomerSearchScore(customer, searchQuery),
        }))
        .filter(({ score }) => score > 0)
        .sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          return String(a.customer?.name || '').localeCompare(String(b.customer?.name || ''), 'pt', {
            sensitivity: 'base',
          });
        })
        .map(({ customer }) => customer);

  const filteredCustomers = forcedCustomerId
    ? baseFilteredCustomers.filter(c => c.id === forcedCustomerId)
    : baseFilteredCustomers;

  const openModal = (customer = null) => {
    if (customer) {
      setEditingCustomer(customer);
      setFormData({
        name: customer.name,
        phone: customer.phone || '',
        email: customer.email || '',
        nif: customer.nif || '',
        address: customer.address || '',
        notes: customer.notes || '',
      });
    } else {
      setEditingCustomer(null);
      setFormData(initialFormState);
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingCustomer) {
        await customersAPI.update(editingCustomer.id, formData);
        toast.success('Cliente atualizado');
      } else {
        await customersAPI.create(formData);
        toast.success('Cliente criado');
      }

      setShowModal(false);
      loadCustomers();
    } catch (error) {
      toast.error('Erro ao guardar cliente');
    } finally {
      setSaving(false);
    }
  };

  const openVoucherModal = (customer) => {
    setSelectedCustomer(customer);
    setVoucherForm({ value: 5, is_percentage: false });
    setShowVoucherModal(true);
  };

  const createVoucher = async () => {
    if (!selectedCustomer) return;
    setSaving(true);

    try {
      const code = `V${Date.now().toString(36).toUpperCase()}`;
      await vouchersAPI.create({
        customer_id: selectedCustomer.id,
        code,
        value: voucherForm.value,
        is_percentage: voucherForm.is_percentage,
        min_purchase: 0,
      });
      toast.success(`Voucher criado: ${code}`);
      setShowVoucherModal(false);
    } catch (error) {
      toast.error('Erro ao criar voucher');
    } finally {
      setSaving(false);
    }
  };


  const loadReceiptBackendPdfForModal = useCallback(async (saleId) => {
    if (!saleId) {
      setReceiptPdfUrl('');
      return;
    }

    setReceiptPdfLoading(true);
    try {
      const response = await salesAPI.getReceiptPDF(saleId, 'receipt');
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const nextUrl = window.URL.createObjectURL(blob);

      setReceiptPdfUrl((previousUrl) => {
        if (previousUrl) {
          window.URL.revokeObjectURL(previousUrl);
        }
        return nextUrl;
      });
    } catch (error) {
      console.error('Erro ao carregar pré-visualização PDF do talão:', error);
      setReceiptPdfUrl('');
      toast.error(error?.response?.data?.detail || 'Erro ao carregar pré-visualização do talão');
    } finally {
      setReceiptPdfLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showReceiptModal && completedSaleId) {
      loadReceiptBackendPdfForModal(completedSaleId);
    }

    if (!showReceiptModal) {
      setReceiptPdfUrl((previousUrl) => {
        if (previousUrl) {
          window.URL.revokeObjectURL(previousUrl);
        }
        return '';
      });
      setReceiptPdfLoading(false);
    }
  }, [showReceiptModal, completedSaleId, loadReceiptBackendPdfForModal]);

    const buildReceiptPreviewPdfPayload = async () => {
      if (!receiptPreviewRef.current) {
        throw new Error('Pré-visualização do talão ainda não está pronta');
      }

      const docNumber =
        receiptData?.sale?.vendus_invoice_number ||
        receiptData?.sale?.ncp_number ||
        completedSaleId ||
        'talao';

      return await buildReceiptPreviewPdfFromElement(
        receiptPreviewRef.current,
        docNumber
      );
    };

    const sendReceiptPreviewEmail = async (saleId, recipientEmail) => {
      const email = String(recipientEmail || '').trim();

      if (!saleId) throw new Error('Venda sem identificador');
      if (!email) throw new Error('Indique um email');

      // Blindagem: o anexo do talão é sempre gerado no backend.
      // Nunca usar PDF criado a partir do HTML da pré-visualização.
      await salesAPI.sendEmail(saleId, email);
    };

    const loadSaleIntoReceiptModal = async (sale, fallbackEmail = '') => {
      if (!sale?.id) throw new Error('Venda sem identificador');

      const receiptRes = await salesAPI.getReceipt(sale.id);
      const receipt = receiptRes?.data || null;

      setReceiptData(receipt);
      setCompletedSaleId(sale.id);
      setSaleEmail(
        receipt?.sale?.customer_email ||
        sale?.customer_email ||
        fallbackEmail ||
        selectedCustomer?.email ||
        ''
      );
      setIsFiscalSale(
        !!receipt?.sale?.vendus_invoice_id ||
        String(receipt?.sale?.invoice_type || '').toLowerCase() === 'fiscal'
      );
      setShowReceiptModal(true);

      await new Promise((resolve) => setTimeout(resolve, 300));
      return receipt;
    };

    const openReceiptPreviewPrintWindow = async () => {
      if (!completedSaleId) {
        throw new Error('Venda sem identificador');
      }

      // Blindagem: impressão sempre pelo PDF backend 80mm.
      const response = await salesAPI.getReceiptPDF(completedSaleId, 'receipt');
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const win = window.open(blobUrl, '_blank');

      if (!win) throw new Error('O navegador bloqueou a abertura da janela');

      toast.success('Talão pronto a imprimir');
    };

    const downloadReceiptPreviewPdf = async () => {
      if (!completedSaleId) {
        throw new Error('Venda sem identificador');
      }

      // Blindagem: descarregar/abrir sempre o PDF backend 80mm.
      const response = await salesAPI.getReceiptPDF(completedSaleId, 'receipt');
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    };

    const openSaleDetail = async (sale) => {
      try {
        if (!sale?.id) {
          toast.error('Venda sem identificador');
          return;
        }

        const saleRes = await salesAPI.getById(sale.id);
        const fullSale = saleRes?.data || null;

        if (!fullSale) {
          toast.error('Não foi possível carregar o detalhe da venda');
          return;
        }

        setSelectedSale(fullSale);
        setSaleEmail(String(fullSale.customer_email || selectedCustomer?.email || '').trim());
        setShowSaleDetail(true);
      } catch (error) {
        console.error('Erro ao abrir detalhe da venda:', error);
        toast.error('Erro ao abrir detalhe da venda');
      }
    };

    const handleViewReceiptModal = async (sale) => {
      try {
        await loadSaleIntoReceiptModal(sale, saleEmail || sale?.customer_email || selectedCustomer?.email || '');
      } catch (error) {
        console.error('Erro ao abrir talão:', error);
        toast.error('Erro ao abrir talão');
      }
    };

    const handleSendSaleEmail = async (sale) => {
      const email = String(saleEmail || sale?.customer_email || selectedCustomer?.email || '').trim();

      if (!sale?.id) {
        toast.error('Venda sem identificador');
        return;
      }

      if (!email) {
        toast.error('Indique um email para envio');
        return;
      }

      try {
        setSendingSaleEmail(true);
        await loadSaleIntoReceiptModal(sale, email);
        await sendReceiptPreviewEmail(sale.id, email);
        toast.success('Email enviado com sucesso');
      } catch (error) {
        console.error('Erro ao enviar email:', error);
        toast.error(error?.response?.data?.detail || error?.message || 'Erro ao enviar email');
      } finally {
        setSendingSaleEmail(false);
      }
    };

  const viewHistory = async (customer) => {
    setSelectedCustomer(customer);
    try {
      const response = await customersAPI.getHistory(customer.id);
      setCustomerHistory(response.data || []);
      setShowHistoryModal(true);
    } catch (error) {
      toast.error('Erro ao carregar histórico');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <SelectCustomerModal
        open={phoneResolveOpen}
        phone={phoneResolvePhone}
        matches={phoneResolveMatches}
        onClose={() => {
          setPhoneResolveOpen(false);
          setPhoneResolveMatches([]);
          setPhoneResolvePhone('');
        }}
        onSelect={(c) => {
          setForcedCustomerId(c.id);
          setPhoneResolveOpen(false);
          setPhoneResolveMatches([]);
          setPhoneResolvePhone('');
        }}
      />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Manrope']">Clientes</h1>
          <p className="text-slate-400">Gestão de clientes e fidelização</p>
        </div>
        <Button
          onClick={() => openModal()}
          className="bg-emerald-500 hover:bg-emerald-600"
          data-testid="add-customer-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Novo Cliente
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-dark p-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <Users className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Total Clientes</p>
              <p className="text-2xl font-bold text-white">{customers.length}</p>
            </div>
          </div>
        </Card>
        <Card className="card-dark p-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Gift className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Pontos Ativos</p>
              <p className="text-2xl font-bold text-white">
                {customers.reduce((sum, c) => sum + (c.points || 0), 0)}
              </p>
            </div>
          </div>
        </Card>
        <Card className="card-dark p-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <Euro className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Total Gasto</p>
              <p className="text-2xl font-bold text-white">
                €{customers.reduce((sum, c) => sum + (c.total_spent || 0), 0).toFixed(0)}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input
          placeholder="Pesquisar cliente..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-slate-800 border-slate-700"
          data-testid="search-customers"
        />
      </div>

      {/* Customers Table */}
      <Card className="card-dark">
        <Table className="table-dark">
          <TableHeader>
            <TableRow>
              <TableHead>Cliente</TableHead>
              <TableHead>Contacto</TableHead>
              <TableHead>NIF</TableHead>
              <TableHead className="text-right">Pontos</TableHead>
              <TableHead className="text-right">Total Gasto</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredCustomers.map((customer) => (
              <TableRow key={customer.id} data-testid={`customer-row-${customer.id}`}>
                <TableCell className="font-medium text-white">
                  {customer.name}
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    <p className="text-slate-300">{customer.phone || '-'}</p>
                    <p className="text-slate-500">{customer.email || '-'}</p>
                  </div>
                </TableCell>
                <TableCell className="text-slate-400">
                  {customer.nif || '-'}
                </TableCell>
                <TableCell className="text-right">
                  <Badge className="badge-success">
                    {customer.points || 0} pts
                  </Badge>
                </TableCell>
                <TableCell className="text-right font-medium text-emerald-400">
                  €{(customer.total_spent || 0).toFixed(2)}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => viewHistory(customer)}
                      title="Ver histórico"
                      data-testid={`history-${customer.id}`}
                    >
                      <ShoppingBag className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openVoucherModal(customer)}
                      title="Criar voucher"
                      data-testid={`voucher-${customer.id}`}
                    >
                      <Gift className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openModal(customer)}
                      data-testid={`edit-customer-${customer.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filteredCustomers.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhum cliente encontrado</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Customer Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingCustomer ? 'Editar Cliente' : 'Novo Cliente'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-slate-300">Nome</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="bg-slate-800 border-slate-700"
                required
                data-testid="customer-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Telefone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                  data-testid="customer-phone"
                />
              </div>
              <div>
                <Label className="text-slate-300">NIF</Label>
                <Input
                  value={formData.nif}
                  onChange={(e) => setFormData(prev => ({ ...prev, nif: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                  data-testid="customer-nif"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="bg-slate-800 border-slate-700"
                data-testid="customer-email"
              />
            </div>

            <div>
              <Label className="text-slate-300">Morada</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                className="bg-slate-800 border-slate-700"
                data-testid="customer-address"
              />
            </div>

            <div>
              <Label className="text-slate-300">Notas</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-slate-800 border-slate-700"
                data-testid="customer-notes"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                className="border-slate-700"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-emerald-500 hover:bg-emerald-600"
                data-testid="save-customer-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Voucher Modal */}
      <Dialog open={showVoucherModal} onOpenChange={setShowVoucherModal}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Criar Voucher</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {selectedCustomer && (
              <p className="text-slate-400">
                Cliente: <span className="text-white">{selectedCustomer.name}</span>
              </p>
            )}

            <div>
              <Label className="text-slate-300">Valor</Label>
              <Input
                type="number"
                min="1"
                value={voucherForm.value}
                onChange={(e) => setVoucherForm(prev => ({ ...prev, value: parseFloat(String(e.target.value).replace(',', '.')) || 0 }))}
                className="bg-slate-800 border-slate-700"
                data-testid="voucher-value"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_percentage"
                checked={voucherForm.is_percentage}
                onChange={(e) => setVoucherForm(prev => ({ ...prev, is_percentage: e.target.checked }))}
                className="rounded border-slate-700 bg-slate-800"
              />
              <Label htmlFor="is_percentage" className="text-slate-300">
                Valor em percentagem (%)
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVoucherModal(false)} className="border-slate-700">
              Cancelar
            </Button>
            <Button onClick={createVoucher} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600" data-testid="create-voucher-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Gift className="w-4 h-4 mr-2" />}
              Criar Voucher
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Modal */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">
              Histórico de Compras - {selectedCustomer?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="max-h-96 overflow-y-auto">
            {customerHistory.length === 0 ? (
              <p className="text-center py-8 text-slate-500">Nenhuma compra registada</p>
            ) : (
              <Table className="table-dark">
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Tipo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customerHistory.map((sale) => (
                    <TableRow
                      key={sale.id}
                      className="cursor-pointer hover:bg-slate-800/70"
                      onClick={() => openSaleDetail(sale)}
                    >
                      <TableCell className="text-slate-300">
                        {new Date(sale.completed_at || sale.created_at).toLocaleDateString('pt-PT')}
                      </TableCell>
                      <TableCell>{sale.items?.length || 0} items</TableCell>
                      <TableCell className="text-right font-medium text-emerald-400">
                        €{sale.total.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={sale.historical_vendus ? 'border-violet-500/50 text-violet-300' : 'border-slate-600'}>
                          {sale.historical_vendus ? 'Histórico Vendus' : sale.invoice_type}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

    <Dialog open={showSaleDetail} onOpenChange={setShowSaleDetail}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            {selectedSale?.historical_vendus ? (
              <Badge className="bg-violet-500/20 text-violet-300 border-violet-500/30">Histórico Vendus</Badge>
            ) : selectedSale?.vendus_invoice_id ? (
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Fiscal</Badge>
            ) : (
              <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Interno</Badge>
            )}
            {selectedSale?.vendus_invoice_number || selectedSale?.ncp_number || 'Detalhes da Venda'}
          </DialogTitle>
        </DialogHeader>

        {selectedSale && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm bg-slate-800/50 rounded-lg p-4">
              <div><span className="text-slate-400">Data:</span><span className="ml-2 text-white">{new Date(selectedSale.completed_at || selectedSale.created_at).toLocaleString('pt-PT')}</span></div>
              <div><span className="text-slate-400">Pagamento:</span><span className="ml-2 text-white">{PAYMENT_LABELS[selectedSale.payment_method] || selectedSale.payment_method || "-"}</span></div>
              <div><span className="text-slate-400">Cliente:</span><span className="ml-2 text-white">{selectedSale.customer_name || selectedCustomer?.name || 'Consumidor Final'}</span></div>
              <div><span className="text-slate-400">Documento:</span><span className="ml-2 text-white">{selectedSale.vendus_invoice_number || selectedSale.ncp_number || selectedSale.id}</span></div>
            </div>

            <Card className="card-dark p-4">
              <Table className="table-dark">
                <TableHeader>
                  <TableRow>
                    <TableHead>Artigo</TableHead>
                    <TableHead className="text-center">Qtd</TableHead>
                    <TableHead className="text-right">Preço</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(selectedSale.items || []).map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="text-white text-sm">{item.product_name}</TableCell>
                      <TableCell className="text-center text-slate-300">{item.quantity}</TableCell>
                      <TableCell className="text-right text-slate-300">€{Number(item.unit_price || 0).toFixed(2)}</TableCell>
                      <TableCell className="text-right text-white font-medium">€{Number(item.total || 0).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>

            <div className="space-y-1 text-sm px-2">
              {(selectedSale.discount_total || 0) > 0 && (
                <div className="flex justify-between text-red-400"><span>Descontos:</span><span>-€{Number(selectedSale.discount_total || 0).toFixed(2)}</span></div>
              )}
              <div className="flex justify-between text-slate-300"><span>IVA:</span><span>€{Number(selectedSale.vat_total || 0).toFixed(2)}</span></div>
              <div className="flex justify-between text-lg font-bold text-white border-t border-slate-600 pt-2 mt-2"><span>TOTAL:</span><span>€{Number(selectedSale.total || 0).toFixed(2)}</span></div>
            </div>

            {!selectedSale.historical_vendus && <div className="space-y-3 pt-2">
              <div>
                <label className="block text-sm text-slate-300 mb-2">Enviar por email</label>
                <Input
                  type="email"
                  value={saleEmail}
                  onChange={(e) => setSaleEmail(e.target.value)}
                  placeholder="Email do cliente"
                  className="bg-slate-800 border-slate-700 text-white"
                />
                <p className="text-xs text-slate-500 mt-2">
                  Se o cliente tiver email registado, aparece preenchido automaticamente.
                </p>
              </div>
            </div>}
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2 pt-2">
          {selectedSale?.historical_vendus ? (
            <div className="mr-auto rounded-md border border-violet-500/30 bg-violet-500/10 px-3 py-2 text-sm text-violet-200">
              Consulta histórica; sem impacto em stock, caixa ou pontos.
            </div>
          ) : <Button onClick={() => handleViewReceiptModal(selectedSale)} className="bg-emerald-600 hover:bg-emerald-700">
            <Receipt className="w-4 h-4 mr-2" />Talão
          </Button>}
          {!selectedSale?.historical_vendus && <Button
            variant="outline"
            onClick={() => handleSendSaleEmail(selectedSale)}
            className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
            disabled={sendingSaleEmail}
          >
            {sendingSaleEmail ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />A enviar...
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-2" />Enviar por email
              </>
            )}
          </Button>}
          <Button variant="outline" onClick={() => setShowSaleDetail(false)} className="border-slate-700">Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
      <DialogContent className="modal-light w-[min(96vw,640px)] max-w-[640px] max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle className="text-slate-900 flex items-center gap-2">
            <Receipt className="w-5 h-5 text-emerald-400" />
            Talão de Venda
          </DialogTitle>
        </DialogHeader>

          {receiptPdfUrl ? (
            <div className="bg-white rounded-lg overflow-hidden mx-auto border border-slate-200" style={{ width: '88mm', maxWidth: '100%', height: '72vh' }}>
              {/* Pré-visualização oficial do talão backend: igual ao PDF de impressão/email */}
              <iframe
                title="Pré-visualização do talão PDF"
                src={receiptPdfUrl}
                className="w-full h-full border-0 bg-white"
              />
            </div>


          ) : receiptPdfLoading ? (
            <div className="bg-white rounded-lg mx-auto border border-slate-200 flex flex-col items-center justify-center gap-3" style={{ width: '88mm', maxWidth: '100%', height: '72vh' }}>
              <Loader2 className="w-7 h-7 animate-spin text-emerald-600" />
              <p className="text-sm text-slate-700">A carregar talão oficial...</p>
            </div>


          ) : receiptData && (
            <div ref={receiptPreviewRef} className="bg-white text-black p-4 rounded-lg font-mono text-sm w-[80mm] mx-auto" id="receipt-content">
              <div className="text-center mb-3">
                {receiptData.company?.logo_url && (
                  <img
                    src={receiptData.company.logo_url}
                    alt="Logo"
                    className="h-14 mx-auto mb-2 object-contain"
                  />
                )}
                <p className="font-bold text-[17px] leading-tight whitespace-nowrap tracking-[-0.2px]">{receiptData.company?.name || 'Ervanária'}</p>
                {receiptData.company?.address && (
                  <p className="text-[11px] leading-tight text-gray-800">{receiptData.company.address}</p>
                )}
                {receiptData.company?.nif && (
                  <p className="text-[11px] leading-tight text-gray-800">NIF: {receiptData.company.nif}</p>
                )}
                {(receiptData.company?.phone || receiptData.company?.email || receiptData.company?.website) && (
                  <p className="text-[11px] leading-tight text-gray-800">
                    {receiptData.company.phone && `Tel: ${receiptData.company.phone}`}
                    {receiptData.company.phone && receiptData.company.email && ' | '}
                    {receiptData.company.email}
                    {(receiptData.company.phone || receiptData.company.email) && receiptData.company.website && ' | '}
                    {receiptData.company.website}
                  </p>
                )}
                {receiptData.company?.capital && (
                  <p className="text-[11px] leading-tight text-gray-800">
                    Capital Social: €{receiptData.company.capital}
                  </p>
                )}
              </div>

              <div className="border-t border-b border-gray-300 py-2 my-2 text-left">
                <p className="text-[11px] font-medium text-black">
                  Fatura Venda {receiptData.sale?.vendus_invoice_number || receiptData.sale?.ncp_number || '-'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2 text-[11px] mb-2">
                <div className="leading-tight">
                  <div className="text-gray-700">Data:</div>
                  <div className="text-black font-medium">
                    {receiptData.sale?.completed_at ? new Date(receiptData.sale.completed_at).toLocaleDateString('pt-PT') : '-'}
                  </div>
                </div>
                <div className="leading-tight text-right">
                  <div className="text-gray-700">Op:</div>
                  <div className="text-black font-medium">{receiptData.sale?.operator_name || '-'}</div>
                </div>
              </div>

              <div className="text-[11px] mb-2 leading-tight">
                <div><span className="text-gray-700">Cliente:</span> <span className="font-medium text-black">{receiptData.sale?.customer_name || 'Consumidor Final'}</span></div>
                <div><span className="text-gray-700">Saldo Pontos Conta Cliente:</span> <span className="font-medium text-black">{receiptData.sale?.customer_points ?? '-'}</span></div>
              </div>

              <div className="border-t border-gray-300 pt-2 mt-2">
                <table className="w-full text-[11px] table-fixed">
                  <colgroup>
                    <col style={{ width: '48%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '20%' }} />
                  </colgroup>
                  <thead>
                    <tr className="border-b border-gray-300 text-gray-700">
                      <th className="text-left py-1 font-bold">Artigo</th>
                      <th className="text-right py-1 font-bold">Qtd</th>
                      <th className="text-right py-1 font-bold">Preço</th>
                      <th className="text-right py-1 font-bold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receiptData.sale?.items?.map((item, idx) => (
                      <tr key={idx} className="border-b border-gray-100 align-top">
                        <td className="py-1 pr-2 text-black break-words">
                          <div>{item.product_name}</div>
                          {(item.lot_number || item.batch_expiry_date || item.expiry_date) && (
                            <div className="text-[9px] text-gray-600 leading-tight mt-0.5 whitespace-nowrap">
                              {item.lot_number ? `Lote: ${item.lot_number}` : ''}
                              {item.lot_number && (item.batch_expiry_date || item.expiry_date) ? ' | ' : ''}
                              {(item.batch_expiry_date || item.expiry_date) ? `Val: ${item.batch_expiry_date || item.expiry_date}` : ''}
                            </div>
                          )}
                        </td>
                        <td className="text-right py-1 text-black">{item.quantity}</td>
                        <td className="text-right py-1 text-black">€{(item.unit_price || 0).toFixed(2)}</td>
                        <td className="text-right py-1 text-black font-medium">€{(item.total || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="border-t border-gray-300 pt-2 mt-2 space-y-1 text-[11px]">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>€{(receiptData.sale?.subtotal || 0).toFixed(2)}</span>
                </div>

                {(receiptData.sale?.discount_total || 0) > 0 && (
                  <div className="flex justify-between text-red-700">
                    <span>Descontos:</span>
                    <span>-€{receiptData.sale.discount_total.toFixed(2)}</span>
                  </div>
                )}

                {(receiptData.sale?.voucher_discount || 0) > 0 && (
                  <div className="flex justify-between text-red-700">
                    <span>Voucher ({receiptData.sale.voucher_code}):</span>
                    <span>-€{receiptData.sale.voucher_discount.toFixed(2)}</span>
                  </div>
                )}

                <div className="flex justify-between">
                  <span>IVA:</span>
                  <span>€{(receiptData.sale?.vat_total || 0).toFixed(2)}</span>
                </div>

                {(Number(receiptData.sale?.prepaid_amount || 0) > 0) && (
                  <div className="flex justify-between text-emerald-700">
                    <span>Pré-pagamento:</span>
                    <span>-€{Number(receiptData.sale?.prepaid_amount || 0).toFixed(2)}</span>
                  </div>
                )}

                {(Number(receiptData.sale?.prepaid_amount || 0) > 0) && !!receiptData.sale?.prepaid_method && (
                  <div className="flex justify-between text-gray-700">
                    <span>Método pré-pago:</span>
                    <span>{String(receiptData.sale?.prepaid_method || '').toUpperCase()}</span>
                  </div>
                )}

                <div className="flex justify-between font-bold text-[16px] border-t border-gray-300 pt-1 mt-1">
                  <span>TOTAL:</span>
                  <span>€{(receiptData.sale?.total || 0).toFixed(2)}</span>
                </div>

                {(Number(receiptData.sale?.prepaid_amount || 0) > 0) && (
                  <div className="flex justify-between font-bold text-[13px]">
                    <span>Falta pagar:</span>
                    <span>
                      €{Number(
                        receiptData.sale?.remaining_paid_at_pos ??
                        Math.max(
                          0,
                          Number(receiptData.sale?.total || 0) - Number(receiptData.sale?.prepaid_amount || 0)
                        )
                      ).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>

              <div className="mt-3 pt-2 border-t border-gray-300 text-[11px] text-center">
                <p>
                  Pagamento: {
                    PAYMENT_LABELS[receiptData.sale?.payment_method] || receiptData.sale?.payment_method || '-'
                  }
                </p>
              </div>

              {(receiptData.sale?.qr_data || receiptData.sale?.atcud || receiptData.sale?.ncp_number) && (
                <div className="mt-3 pt-2 border-t border-gray-300 text-center">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(receiptData.sale?.qr_data || receiptData.sale?.atcud || receiptData.sale?.ncp_number || '')}`}
                    alt="QR"
                    className="w-28 h-28 mx-auto"
                  />
                  <p className="text-[10px] text-gray-500 mt-1">QR / ATCUD</p>
                </div>
              )}

              <div className="text-center mt-4 pt-2 border-t border-gray-300 text-[10px] leading-tight">
                <p className="italic">{receiptData.company?.receipt_footer_message || 'Obrigado pela sua preferência!'}</p>
                {isFiscalSale ? (
                  <p className="mt-1 text-emerald-700 font-bold text-[10px]">Documento fiscal emitido com sucesso.</p>
                ) : (
                  <p className="mt-1 text-gray-500 text-[10px]">Documento interno.</p>
                )}
              </div>
            </div>
          )}
        )}

        <div className="space-y-2 px-1">
          <label className="text-slate-700 text-sm">Enviar por email</label>
          <Input
            type="email"
            value={saleEmail}
            onChange={(e) => setSaleEmail(e.target.value)}
            placeholder="Email do cliente"
            className="bg-white border-slate-300 text-slate-900 w-full"
            style={{ color:'#0f172a', caretColor:'#0f172a', WebkitTextFillColor:'#0f172a' }}
          />
        </div>

        <DialogFooter className="gap-2 grid grid-cols-1 md:grid-cols-2">
          <Button
            variant="outline"
            onClick={() => setShowReceiptModal(false)}
            className="w-full border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900"
          >
            Fechar
          </Button>

          <Button
            variant="outline"
            onClick={async () => {
              const email = String(saleEmail || '').trim();
              if (!email) {
                toast.error('Indique um email para envio');
                return;
              }
              if (!completedSaleId) {
                toast.error('Venda sem identificador');
                return;
              }
              try {
                setSendingSaleEmail(true);
                await sendReceiptPreviewEmail(completedSaleId, email);
                toast.success('Email enviado com sucesso');
              } catch (error) {
                console.error('Erro ao enviar email:', error);
                toast.error(error?.response?.data?.detail || error?.message || 'Erro ao enviar email');
              } finally {
                setSendingSaleEmail(false);
              }
            }}
            disabled={sendingSaleEmail || !completedSaleId}
            className="w-full border-blue-500 text-blue-700 hover:bg-blue-50"
          >
            {sendingSaleEmail ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                A enviar...
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-2" />
                Enviar por email
              </>
            )}
          </Button>

          <Button
            variant="outline"
            onClick={async () => {
              try {
                await downloadReceiptPreviewPdf();
                toast.success('Talão PDF aberto no navegador');
              } catch (error) {
                console.error('Erro ao gerar talão PDF:', error);
                toast.error(error?.message || 'Erro ao gerar PDF');
              }
            }}
            className="w-full border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900"
          >
            <FileText className="w-4 h-4 mr-2" />
            Talão PDF
          </Button>

          <Button
            onClick={async () => {
              try {
                await openReceiptPreviewPrintWindow();
              } catch (error) {
                console.error('Erro ao imprimir talão:', error);
                toast.error(error?.message || 'Erro ao abrir talão para impressão');
              }
            }}
            className="bg-emerald-500 hover:bg-emerald-600"
          >
            <Receipt className="w-4 h-4 mr-2" />
            Imprimir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </div>
  );
};

export default CustomersPage;
